<template>
  <q-table
    :title="title"
    :hideTitle="hideTitle"
    :data="summaryData"
    :columns="summaryColumns"
    :visible-columns="[...visibleColumns, 'group']"
    dense
    hide-bottom
    :pagination="{rowsPerPage: 0}"
    row-key="group">

    <template v-slot:top>
      <span v-if="!hideTitle" class="text-h6">{{title}}</span>
      <q-space />
      <q-select
        v-model="visibleColumns"
        borderless
        v-show="false"
        dense
        multiple
        options-dense
        display-value=""
        :options="columns"
      />
    </template>

    <template v-slot:body-cell-group="props">
      <q-td :props="props">
        <slot name="group" v-bind:row="props.row">
          {{ props.value }}
        </slot>
      </q-td>
    </template>

    <template v-slot:body-cell-custom="props">
      <q-td :props="props">
        <slot name="custom" v-bind:row="props.row">
          {{ props.value }}
        </slot>
      </q-td>
    </template>

  </q-table>
</template>

<script>
import { formatCurrency } from '../util/string'
import getNestedFieldInObject from '@/util/getNestedFieldInObject'

export default {
  name: 'summaryTable',
  props: {
    title: String,
    hideTitle: Boolean,
    storeType: String,
    items: {
      type: Array,
      required: true
    },
    groupName: String,
    groupFieldCallback: Function,
    sumCallback: Function,
    sumName: String,
    sortCallback: Function,
    hidePriceColumn: Boolean
  },
  computed: {
    priceFieldName () {
      return {
        'container': 'containerPrice',
        'retail': 'retailPrice',
        'stock': 'price'
      }[this.storeType] || 'price'
    },
    summaryDataCustom () {
      return this.summariseByGroup(this.groupFieldCallbackComputed, this.sumCallbackComputed)
        .sort(this.sortCallbackComputed)
        .filter(d => d.value)
    },
    summaryDataHolds () {
      return this.summariseByGroup(this.groupFieldCallbackComputed,
        this.sumCallbackComputed || (
          (row) => (getNestedFieldInObject('sku.product.attributes.holds', row) || 0) * row.qty
        ))
        .sort(this.sortCallbackComputed)
        .filter(d => d.value)
    },
    summaryDataPrice () {
      return this.summariseByGroup(this.groupFieldCallbackComputed,
        (row) => {
          // Always return zero if there are no holds per qty.
          if (row.qty === 0) {
            return 0
          }
          if (row.totalCost) {
            return row.totalCost
          }
          return (((row.itemCost || row.sku?.[this.priceFieldName]) || 0) * row.qty)
        })
        .sort(this.sortCallbackComputed)
        .filter(d => d.value)
    },
    summaryData () {
      return this.mergeColumns({
        holds: this.summaryDataHolds,
        price: this.summaryDataPrice
      })
    },
    sumCallbackComputed () {
      // Can't return a function from 'default' in props unfortunately so this is my workaround
      return this.sumCallback
    },
    sortCallbackComputed () {
      // Can't return a function from 'default' in props unfortunately so this is my workaround
      return this.sortCallback ||
        ((a, b) => a['group'] < b['group'] ? -1 : a['group'] > b['group'] ? 1 : 0)
    },
    groupFieldCallbackComputed () {
      // Can't return a function from 'default' in props unfortunately so this is my workaround
      const func = this.groupFieldCallback ||
        (row => getNestedFieldInObject(`sku.product.attributes.${this.groupNameComputed.toLowerCase()}`, row))
      return v => func(v) || 'Other'
    },
    groupNameComputed () {
      return this.groupName || this.title
    },
    sumColumnName () {
      return this.sumName || 'Holds'
    },
    summaryColumns () {
      return [
        { name: 'group', label: this.groupNameComputed, align: 'left', field: row => row.group, sortable: false },
        { name: 'Holds', label: this.sumColumnName, align: 'right', field: row => row.holds, sortable: false },
        ...!this.hidePriceColumn ? [{ name: 'Price', label: 'Price', align: 'right', field: row => row.price, sortable: false, format: formatCurrency }] : [],
        { name: 'Custom', label: '', align: 'right', field: row => row.custom, sortable: false }
        // { name: 'Cost Per Hold', label: 'Cost Per Hold', align: 'right', field: row => row.costPerHold, sortable: false, format: formatCurrency }
      ]
    }
  },
  methods: {
    /**
     *
     * @param columns
     * @returns {Array|*}
     */
    mergeColumns (columns) {
      if (!Object.keys(columns).length) return []
      let result = []
      for (const colName in columns) {
        for (const row of columns[colName]) {
          let resultRow = result.find(resultRow => resultRow.group === row.group)
          if (!resultRow) {
            resultRow = { group: row.group }
            if (row.item) resultRow.item = row.item
            result.push(resultRow)
          }
          resultRow[colName] = row.value
        }
      }
      return result
    },
    summariseByGroup (groupFieldCallback, sumCallback) {
      let map = this.items?.reduce?.((result, item) => {
        let groups = groupFieldCallback(item)
        let values = sumCallback(item)

        // Convert single item into array
        if (!Array.isArray(groups)) groups = [groups]
        if (!Array.isArray(values)) values = Array.from({ length: groups.length }, () => values)
        if (groups.length !== values.length) throw Error('Invalid lengths!')

        for (let i = 0; i < groups.length; i++) {
          const group = groups[i]
          const value = values[i]
          if (!(group in result)) {
            result[group] = { item, value: 0 }
          }
          result[group].value += value
        }

        let hardwareType = ["hardware","climbing"];

        if (this.title == "Colour" && hardwareType.some(el => item.sku.product.attributes.type.toLowerCase().includes(el))){
          delete result[item.sku.options.colour.group];
        }  
        return result;
      }, {})
      return Object.keys(map).map(key => { return { group: key, value: map[key].value, item: map[key].item } })
    }
  },
  data () {
    return {
      visibleColumns: ['Holds', 'Price'],
      columns: ['Holds', 'Price']
    }
  }
}
</script>

<style scoped>

</style>
